import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Search, Loader2, Wallet, Check, AlertCircle, User, FileText } from 'lucide-react';
import { bousanmSolideApi } from '../services/bousanmSolideApi';

const NewDepotModal = ({ open, onOpenChange, onSuccess }) => {
  const { t } = useTranslation(['solide', 'common']);
  const [step, setStep] = useState(1); // 1: Search, 2: Configure
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);

  const [accountInfo, setAccountInfo] = useState(null);
  const [infoLoading, setInfoLoading] = useState(false);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);

  const [agentsList, setAgentsList] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [agentsLoading, setAgentsLoading] = useState(false);

  const [planSol, setPlanSol] = useState('');
  const [nbSemaines, setNbSemaines] = useState(1);
  const [description, setDescription] = useState('');
  const [dateDebut, setDateDebut] = useState(new Date().toISOString().split('T')[0]);
  const [isOnTime, setIsOnTime] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Reset on close
  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setStep(1);
        setSearchTerm('');
        setSearchResults([]);
        setSelectedAccount(null);
        setAccountInfo(null);
        setAvailablePlans([]);
        setAgentsList([]);
        setSelectedAgent('');
        setPlanSol('');
        setNbSemaines(1);
        setDescription('');
        setDateDebut(new Date().toISOString().split('T')[0]);
        setIsOnTime(true);
        setError('');
        setSuccessMessage('');
      }, 300);
    }
  }, [open]);

  // Fetch plans if needed
  useEffect(() => {
    const loadPlans = async () => {
      if (step === 2 && accountInfo?.est_premier_depo && availablePlans.length === 0) {
        setPlansLoading(true);
        try {
          const data = await bousanmSolideApi.getPlansConfiguration();
          // Filter active plans
          const activePlans = (data.plans || []).filter(p => p.actif).sort((a, b) => a.ordre - b.ordre);
          setAvailablePlans(activePlans);
        } catch (err) {
          console.error("Error loading plans:", err);
          setError(t('solide:newDepotModal.messages.plansError'));
        } finally {
          setPlansLoading(false);
        }
      }
    };
    loadPlans();
  }, [step, accountInfo]);

  // Load agents if needed (for first deposit)
  useEffect(() => {
    if (step === 2 && accountInfo?.est_premier_depo) {
      setAgentsLoading(true);
      bousanmSolideApi.getAgentsList()
        .then(data => {
          setAgentsList(data || []);
        })
        .catch(err => console.error("Erreur chargement agents:", err))
        .finally(() => setAgentsLoading(false));
    }
  }, [step, accountInfo]);

  // Search debounced
  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (searchTerm.length >= 2) {
        setSearchLoading(true);
        try {
          // Use backend filters for BS accounts
          const data = await bousanmSolideApi.searchAccounts(searchTerm, {
            type_compte: 'BS',
            etat: 'Actif',
            light: true,
            exclude_temp: true,
            exclude_agent: true
          });

          // Frontend filter as backup/refinement (case insensitive)
          const filtered = (data.results || []).filter(acc => {
            const isActive = acc.type_compte === 'BS' && acc.etat?.toLowerCase() === 'actif';
            const isPendingOnline = acc.onlineCreated === true && (acc.client_statut === 'pending_verification' || acc.statut === 'pending_verification');
            return isActive && !isPendingOnline;
          });
          setSearchResults(filtered);
        } catch (err) {
          console.error("Search error:", err);
          setSearchResults([]);
        } finally {
          setSearchLoading(false);
        }
      } else {
        setSearchResults([]);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  const handleSelectAccount = async (account) => {
    setSelectedAccount(account);
    setInfoLoading(true);
    setError('');

    try {
      const info = await bousanmSolideApi.getDepoSolidaireInfo(account.code);
      setAccountInfo(info);

      // If first deposit, require plan
      if (info.est_premier_depo) {
        setPlanSol(''); // Reset plan selection
      } else if (info.plan_info) {
        setPlanSol(info.plan_info.plan);
      }

      setNbSemaines(1);
      setStep(2);
    } catch (err) {
      console.error("Info error:", err);
      const msg = err.response?.data?.error || t('solide:newDepotModal.messages.infoError');
      setError(msg);
    } finally {
      setInfoLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedAccount) return;
    if (accountInfo?.est_premier_depo && !planSol) {
      setError(t('solide:newDepotModal.messages.selectPlan'));
      return;
    }
    if (nbSemaines < 1 || nbSemaines > 52) {
      setError(t('solide:newDepotModal.messages.weeksError'));
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      // Calculate total amount
      const currentCurrency = selectedAccount?.devise || 'HTG';
      let currentMontantHebdo = 0;

      if (accountInfo?.est_premier_depo) {
        if (planSol) {
          const plan = availablePlans.find(p => p.code.toLowerCase() === planSol.toLowerCase());
          if (plan) {
            currentMontantHebdo = currentCurrency === 'USD' ? plan.montant_hebdo_usd : plan.montant_hebdo_htg;
          }
        }
      } else {
        currentMontantHebdo = accountInfo?.montant_hebdomadaire || 0;
      }

      const totalAmount = currentMontantHebdo * parseInt(nbSemaines);

      const payload = {
        compte_id: accountInfo.compte_id, // UUID obtained from getDepoSolidaireInfo
        compte_code: selectedAccount.code,
        montant: totalAmount,
        nb_semaines: parseInt(nbSemaines),
        description: description || t('solide:newDepotModal.messages.defaultDescription')
      };

      if (accountInfo.est_premier_depo) {
        payload.plan_sol = planSol; // Send the plan code (e.g., 'espwa')
        payload.date_debut = dateDebut;
        payload.is_on_time = isOnTime;
        if (selectedAgent) {
          payload.agent_assigne = selectedAgent;
        }
      }

      const result = await bousanmSolideApi.createDepoSolidaire(payload);

      setSuccessMessage(result.message || t('solide:newDepotModal.messages.success'));

      // Delay closing to show success
      setTimeout(() => {
        onOpenChange(false);
        if (onSuccess) onSuccess(result);
      }, 1500);

    } catch (err) {
      console.error("Submit error:", err);
      const msg = err.response?.data?.error || err.response?.data?.details || t('solide:newDepotModal.messages.submitError');
      setError(typeof msg === 'object' ? JSON.stringify(msg) : msg);
    } finally {
      setSubmitting(false);
    }
  };

  const currency = selectedAccount?.devise || 'HTG'; // Or check accountInfo

  // Calculate totals
  const getMontantHebdo = () => {
    if (accountInfo?.est_premier_depo) {
      // Use selected plan amount
      if (!planSol) return 0;
      const plan = availablePlans.find(p => p.code.toLowerCase() === planSol.toLowerCase());
      if (plan) {
        return currency === 'USD' ? plan.montant_hebdo_usd : plan.montant_hebdo_htg;
      }
      return 0;
    }
    return accountInfo?.montant_hebdomadaire || 0;
  };

  const montantHebdo = getMontantHebdo();
  const totalAmount = montantHebdo * nbSemaines;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] p-0 bg-white flex flex-col max-h-[85vh]">
        <div className="p-3 border-b">
          <DialogTitle className="flex items-center gap-2 text-base text-blue-800">
            <Wallet className="h-5 w-5" />
            {t('solide:newDepotModal.title')}
          </DialogTitle>
        </div>

        <div className="p-3 overflow-y-auto flex-1">
          {step === 1 && (
            <div className="space-y-3">
              <div className="space-y-1">
                <label className="text-[10px] font-medium text-gray-500">{t('solide:newDepotModal.search.label')}</label>
                <div className="relative">
                  <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-gray-400" />
                  <Input
                    placeholder={t('solide:newDepotModal.search.placeholder')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-8 h-8 text-sm"
                    autoFocus
                  />
                  {searchLoading && (
                    <div className="absolute right-2.5 top-2">
                      <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                    </div>
                  )}
                </div>
              </div>

              <div className="border rounded-md max-h-[300px] overflow-y-auto bg-gray-50/50 min-h-[100px]">
                {searchLoading ? (
                  <div className="h-full min-h-[100px] flex flex-col justify-center items-center text-blue-600 gap-2">
                    <Loader2 className="h-6 w-6 animate-spin" />
                    <span className="text-[10px] text-gray-400">{t('solide:newDepotModal.search.loading')}</span>
                  </div>
                ) : searchResults.length > 0 ? (
                  <div className="divide-y divide-gray-100">
                    {searchResults.map((acc) => (
                      <div
                        key={acc.code}
                        onClick={() => handleSelectAccount(acc)}
                        className="p-2.5 hover:bg-blue-50 cursor-pointer transition-colors flex justify-between items-center"
                      >
                        <div>
                          <div className="font-bold text-sm text-gray-900">{acc.nom} {acc.prenom}</div>
                          <div className="text-[10px] text-gray-500 flex gap-2 mt-0.5">
                            <span className="font-mono bg-gray-200 px-1 rounded">{acc.code}</span>
                            <span>{acc.username || acc.client_id}</span>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className={`text-xs font-bold ${acc.devise === 'USD' ? 'text-green-600' : 'text-gray-900'}`}>
                            {parseFloat(acc.solde_PL || acc.solde || 0).toLocaleString()} {acc.devise}
                          </div>
                          <div className="text-[10px] text-gray-400 capitalize">{acc.etat}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-6 text-center text-gray-400 text-xs">
                    {searchTerm.length < 2 ? t('solide:newDepotModal.search.hint') : t('solide:newDepotModal.search.empty')}
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 2 && accountInfo && (
            <div className="space-y-3">
              {/* Account Summary */}
              <div className="bg-blue-50/50 p-2 rounded-md border border-blue-100 flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <div className="h-8 w-8 rounded-full bg-white flex items-center justify-center text-blue-600 font-bold text-xs border border-blue-100">
                    {selectedAccount.prenom?.charAt(0)}{selectedAccount.nom?.charAt(0)}
                  </div>
                  <div>
                    <div className="font-bold text-sm text-blue-900">{selectedAccount.prenom} {selectedAccount.nom}</div>
                    <div className="text-[10px] text-blue-600 font-mono">{selectedAccount.code}</div>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setStep(1)} className="h-6 text-[10px] px-2 text-blue-600 hover:text-blue-800 hover:bg-blue-100">
                  {t('solide:newDepotModal.config.change')}
                </Button>
              </div>

              {accountInfo.est_premier_depo && (
                <div className="bg-amber-50 border border-amber-200 rounded-md p-2.5 flex items-start gap-2.5">
                  <AlertCircle className="h-3.5 w-3.5 text-amber-600 mt-0.5" />
                  <div className="text-amber-800 text-[11px] leading-tight">
                    {t('solide:newDepotModal.config.firstDepotAlert')}
                  </div>
                </div>
              )}

              <div className="grid gap-3">
                {/* Plan Selection (Only if first deposit) */}
                {accountInfo.est_premier_depo && (
                  <div className="space-y-1">
                    <label className="text-[10px] font-medium text-gray-500">{t('solide:newDepotModal.config.planLabel')}</label>
                    {plansLoading ? (
                      <div className="flex justify-center py-2"><Loader2 className="h-4 w-4 animate-spin text-blue-600" /></div>
                    ) : (
                      <div className="grid grid-cols-3 gap-2">
                        {availablePlans.map((plan) => (
                          <div
                            key={plan.code}
                            onClick={() => setPlanSol(plan.code)}
                            className={`cursor-pointer border rounded-md p-2 text-center transition-all ${planSol === plan.code
                                ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                                : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300'
                              }`}
                          >
                            <div className="text-[10px] font-bold uppercase mb-0.5">{plan.nom}</div>
                            <div className="text-sm font-bold">
                              {currency === 'USD' ? plan.montant_hebdo_usd : plan.montant_hebdo_htg}
                              <span className="text-[10px] font-normal ml-0.5">{currency === 'USD' ? '$' : 'G'}</span>
                            </div>
                          </div>
                        ))}
                        {availablePlans.length === 0 && (
                          <div className="col-span-3 text-center text-[10px] text-gray-400">{t('solide:newDepotModal.config.noPlan')}</div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Agent Selection (Optional for first deposit) */}
                {accountInfo.est_premier_depo && (
                  <div className="space-y-1">
                    <label className="text-[10px] font-medium text-gray-500">{t('solide:newDepotModal.config.assignAgent')}</label>
                    {agentsLoading ? (
                      <div className="h-8 flex items-center text-xs text-gray-400"><Loader2 className="h-3 w-3 animate-spin mr-2" /> {t('solide:newDepotModal.config.loading')}</div>
                    ) : (
                      <select
                        value={selectedAgent}
                        onChange={(e) => setSelectedAgent(e.target.value)}
                        className="w-full h-8 text-xs border rounded-md px-2 bg-white text-gray-700 focus:outline-none focus:border-blue-500"
                      >
                        <option value="">{t('solide:newDepotModal.config.noAgent')}</option>
                        {agentsList.map(agent => (
                          <option key={agent.username} value={agent.username}>
                            {agent.first_name} {agent.last_name} ({agent.username})
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                )}

                {/* Date de début et Ponctualité (First deposit only) */}
                {accountInfo.est_premier_depo && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-2.5 border rounded-md bg-white">
                    <div className="space-y-1">
                      <label className="text-[10px] font-medium text-gray-500">{t('solide:newDepotModal.config.dateDebut')}</label>
                      <Input
                        type="date"
                        value={dateDebut}
                        onChange={(e) => setDateDebut(e.target.value)}
                        className="h-8 text-xs"
                      />
                    </div>
                    <div className="flex items-center gap-2 pt-4">
                      <input
                        type="checkbox"
                        id="isOnTime"
                        checked={isOnTime}
                        onChange={(e) => setIsOnTime(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <label htmlFor="isOnTime" className="text-[10px] font-medium text-gray-700 cursor-pointer">
                        {t('solide:newDepotModal.config.isOnTime')}
                      </label>
                    </div>
                  </div>
                )}

                {/* Configuration Financière */}
                <div className="bg-gray-50/50 rounded-md border p-2.5 space-y-2.5">
                  <div className="flex justify-between items-center border-b pb-1.5">
                    <p className="text-[10px] font-bold text-gray-900 uppercase flex items-center gap-1.5">
                      <Wallet className="h-3 w-3" /> {t('solide:newDepotModal.config.header')}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <label className="text-[10px] font-medium text-gray-500">{t('solide:newDepotModal.config.nbWeeks')}</label>
                      <Input
                        type="number"
                        min="1"
                        max="52"
                        value={nbSemaines}
                        onChange={(e) => setNbSemaines(e.target.value)}
                        className="font-bold text-sm h-8 text-center"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-medium text-gray-500">{t('solide:newDepotModal.config.weeklyAmount')}</label>
                      <div className="h-8 px-3 bg-white rounded-md border border-gray-200 text-gray-700 font-bold text-sm flex items-center justify-between">
                        <span>{montantHebdo}</span>
                        <span className="text-[10px] text-gray-400">{currency}</span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] font-medium text-gray-500">{t('solide:newDepotModal.config.description')}</label>
                    <Input
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder={t('solide:newDepotModal.config.descriptionPlaceholder')}
                      className="text-xs h-7"
                    />
                  </div>
                </div>

                {/* Total */}
                <div className="bg-gray-900 text-white p-3 rounded-md flex justify-between items-center">
                  <div className="text-xs text-gray-300">{t('solide:newDepotModal.config.totalToPay')}</div>
                  <div className="text-lg font-bold flex items-baseline gap-1">
                    {totalAmount.toLocaleString()}
                    <span className="text-xs text-gray-400">{currency}</span>
                  </div>
                </div>

                {error && (
                  <div className="text-red-500 text-[10px] bg-red-50 p-2 rounded border border-red-100">
                    {error}
                  </div>
                )}

                {successMessage && (
                  <div className="text-green-600 text-xs bg-green-50 p-2 rounded border border-green-100 flex items-center gap-2 font-bold">
                    <Check className="h-3.5 w-3.5" />
                    {successMessage}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="p-3 border-t mt-0 sm:justify-between gap-2 bg-gray-50/50">
          {step === 2 && (
            <Button variant="ghost" size="sm" onClick={() => setStep(1)} disabled={submitting || successMessage} className="h-8 text-xs">
              {t('solide:newDepotModal.footer.back')}
            </Button>
          )}
          {step === 2 ? (
            <Button
              size="sm"
              onClick={handleSubmit}
              disabled={submitting || !selectedAccount || (accountInfo?.est_premier_depo && !planSol) || successMessage}
              className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto ml-auto h-8 text-xs"
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> {t('solide:newDepotModal.footer.processing')}
                </>
              ) : successMessage ? (
                t('solide:newDepotModal.footer.done')
              ) : (
                t('solide:newDepotModal.footer.confirm')
              )}
            </Button>
          ) : (
            <div className="text-[10px] text-gray-400 w-full text-center pt-1">
              {t('solide:newDepotModal.footer.hint')}
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NewDepotModal;
